import { formatCurrency } from "@/utils/formatCurrency";
import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  WalletCards,
} from "lucide-react";
import { Logo } from "../Logo";

interface DisplayAsset {
  id: string;
  symbol: string;
  balance: string;
  convertedBalance: number;
}

interface Asset {
  id: string;
  token?: {
    symbol?: string;
  } | null;
  balance?: string | number | null;
  convertedBalance?: number | string | null;
}

const PhoneMockUp = () => {
  const currency = "USD";
  const totalBalance = 999.99;

  // Replace with your actual asset data when available.
  const assets: Asset[] = [];

  const previewAssets: DisplayAsset[] = [
    {
      id: "preview-usdt",
      symbol: "USDT",
      balance: "1,250.00",
      convertedBalance: 1250,
    },
    {
      id: "preview-usdc",
      symbol: "USDC",
      balance: "850.00",
      convertedBalance: 850,
    },
    {
      id: "preview-ngn",
      symbol: "NGN",
      balance: "500,000",
      convertedBalance: 350,
    },
    {
      id: "preview-zar",
      symbol: "ZAR",
      balance: "650,000.00",
      convertedBalance: 350,
    },
    {
      id: "preview-usd",
      symbol: "USD",
      balance: "750,000.00",
      convertedBalance: 350,
    },
  ];

  const displayAssets: DisplayAsset[] =
    assets.length > 0
      ? [...assets]
          .sort(
            (a, b) =>
              Number(b.convertedBalance ?? 0) - Number(a.convertedBalance ?? 0),
          )
          .slice(0, 5)
          .map((asset) => ({
            id: asset.id,
            symbol: asset.token?.symbol || "AST",
            balance: String(asset.balance ?? "0"),
            convertedBalance: Number(asset.convertedBalance ?? 0),
          }))
      : previewAssets;

  const floatingCards = [
    {
      icon: ArrowDownToLine,
      label: "Recent deposit",
      title: "Funds received",
      type: "success",
    },
    {
      icon: ArrowLeftRight,
      label: "Currency swap",
      title: "Exchange completed",
      type: "primary",
    },
    {
      icon: ArrowUpRight,
      label: "Recent transfer",
      title: "Transfer completed",
      type: "info",
    },
    {
      icon: ShieldCheck,
      label: "Security",
      title: "Protected account",
      type: "primary",
    },
  ] as const;

  const actions = [
    { icon: ArrowDownToLine, label: "Deposit" },
    { icon: ArrowUpRight, label: "Send" },
    { icon: ArrowLeftRight, label: "Swap" },
  ];

  return (
    <div className="text-foreground">
      <div className="relative flex min-h-[390px] items-center justify-center overflow-hidden px-5 pb-8 pt-2 sm:px-10 lg:min-h-0 lg:py-8">
        {/* Theme-aware background glow */}
        <div className="pointer-events-none absolute right-[10%] top-1/2 size-[280px] -translate-y-1/2 rounded-full bg-primary/10 blur-[2px] sm:size-[360px]" />

        <div className="pointer-events-none absolute right-[15%] top-1/2 size-[240px] -translate-y-1/2 rounded-full border border-primary/15 sm:size-[310px]" />

        {/* Floating information cards */}
        {floatingCards.map(({ icon: Icon, label, title, type }, index) => (
          <div
            key={label}
            className={[
              "absolute z-20 hidden items-center gap-2.5 rounded-2xl",
              "border border-border bg-card/95 px-3.5 py-3",
              "shadow-lg backdrop-blur-xl sm:flex",
              index === 0 ? "left-[5%] top-[18%] lg:left-[3%]" : "",
              index === 1 ? "right-[4%] top-[12%] lg:right-[3%]" : "",
              index === 2 ? "bottom-[13%] left-[4%] lg:left-[3%]" : "",
              index === 3 ? "bottom-[14%] right-[3%]" : "",
            ].join(" ")}
          >
            <span
              className={[
                "flex size-8 shrink-0 items-center justify-center rounded-full",
                type === "success"
                  ? "bg-emerald-500/10"
                  : type === "info"
                    ? "bg-sky-500/10"
                    : "bg-primary/10",
              ].join(" ")}
            >
              <Icon
                className={[
                  "size-3.5",
                  type === "success"
                    ? "text-emerald-600 dark:text-emerald-400"
                    : type === "info"
                      ? "text-sky-600 dark:text-sky-400"
                      : "text-primary",
                ].join(" ")}
              />
            </span>

            <div className="min-w-0">
              <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                {label}
              </p>

              <p className="mt-0.5 whitespace-nowrap text-xs font-bold text-card-foreground">
                {title}
              </p>
            </div>

            {index !== 3 && (
              <CheckCircle2
                className={[
                  "ml-1 size-3.5 shrink-0",
                  type === "success"
                    ? "text-emerald-600 dark:text-emerald-400"
                    : type === "info"
                      ? "text-sky-600 dark:text-sky-400"
                      : "text-primary",
                ].join(" ")}
              />
            )}
          </div>
        ))}

        {/* Phone */}
        <div className="relative z-10 w-[230px] rotate-[4deg] transition-transform duration-700 hover:rotate-0 sm:w-[250px] lg:w-[255px] xl:w-[270px]">
          {/* Outer shell */}
          <div className="relative rounded-[2.8rem] border-[6px] border-border bg-card p-[4px] shadow-[0_35px_80px_-20px_rgba(0,0,0,.5)]">
            {/* Side buttons */}
            <div className="absolute -left-[8px] top-20 h-10 w-[3px] rounded-l-full bg-muted-foreground/60" />
            <div className="absolute -right-[8px] top-24 h-16 w-[3px] rounded-r-full bg-muted-foreground/60" />

            {/* Phone screen */}
            <div className="relative aspect-[9/19] overflow-hidden rounded-[2.25rem] bg-background text-foreground">
              {/* Dynamic island */}
              <div className="absolute left-1/2 top-2 z-20 h-5 w-20 -translate-x-1/2 rounded-full bg-foreground" />

              {/* Screen background */}
              <div className="absolute inset-0 bg-background" />

              {/* Screen glow */}
              <div className="pointer-events-none absolute -right-20 -top-20 size-48 rounded-full bg-primary/15 blur-[45px]" />

              <div className="relative z-10 p-4 pt-9">
                {/* Phone header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Logo
                      size="lg"
                      className="transition-transform duration-300"
                    />
                  </div>

                  <div className="size-5 rounded-full border border-border bg-muted" />
                </div>

                {/* Balance */}
                <div className="mt-7">
                  <p className="text-[7px] font-medium uppercase tracking-[0.15em] text-muted-foreground">
                    Total balance
                  </p>

                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="text-[8px] font-bold text-muted-foreground">
                      {currency}
                    </span>

                    <span className="truncate text-xl font-black tracking-[-0.05em] text-foreground">
                      {formatCurrency(totalBalance)}
                    </span>
                  </div>

                  <div className="mt-2 flex items-center gap-1.5">
                    <span className="rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[6px] font-bold text-emerald-600 dark:text-emerald-400">
                      Available
                    </span>

                    <span className="text-[6px] text-muted-foreground">
                      Updated just now
                    </span>
                  </div>
                </div>

                {/* Phone actions */}
                <div className="mt-5 grid grid-cols-3 gap-1.5">
                  {actions.map(({ icon: Icon, label }) => (
                    <div
                      key={label}
                      className="flex flex-col items-center gap-1 rounded-xl border border-border bg-muted/50 py-2"
                    >
                      <Icon className="size-3 text-primary" />

                      <span className="text-[6px] font-semibold text-muted-foreground">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Assets */}
                <div className="mt-6">
                  <div className="flex items-center justify-between">
                    <span className="text-[8px] font-bold text-foreground/85">
                      Your assets
                    </span>

                    <span className="text-[6px] font-medium text-primary">
                      View all
                    </span>
                  </div>

                  <div className="mt-2 space-y-1.5">
                    {displayAssets.map((asset) => (
                      <div
                        key={asset.id}
                        className="flex items-center justify-between gap-1 rounded-xl border border-border bg-card px-2 py-2"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[7px] font-black text-primary">
                            {asset.symbol.slice(0, 3)}
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-[7px] font-bold text-foreground/85">
                              {asset.symbol}
                            </p>

                            <p className="text-[6px] text-muted-foreground">
                              {asset.balance}
                            </p>
                          </div>
                        </div>

                        <p className="shrink-0 text-[7px] font-bold text-foreground/80">
                          {formatCurrency(asset.convertedBalance)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom navigation */}
              <div className="absolute inset-x-3 bottom-3 flex items-center justify-around rounded-2xl border border-border bg-card/95 px-2 py-2 backdrop-blur-xl">
                <div className="flex flex-col items-center gap-0.5">
                  <WalletCards className="size-3 text-primary" />

                  <span className="text-[5px] font-semibold text-primary">
                    Home
                  </span>
                </div>

                <div className="flex flex-col items-center gap-0.5">
                  <ArrowLeftRight className="size-3 text-muted-foreground" />

                  <span className="text-[5px] text-muted-foreground">
                    Trade
                  </span>
                </div>

                <div className="flex flex-col items-center gap-0.5">
                  <ArrowUpRight className="size-3 text-muted-foreground" />

                  <span className="text-[5px] text-muted-foreground">
                    Transfer
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneMockUp;
